import { forwardRef } from 'react';
import { Center, styled } from '@terra/ui-styled-system';
import { Spinner } from './spinner';
import {
  IconButton as StyledIconButton,
  type IconButtonStyledProps,
} from './styled/icon-button';
import {
  BIEvents,
  type BIEventProps,
  serializeBIEventData,
} from '@terra/ui-telemetry/bi-analytics';

export type { IconButtonSize } from './styled/icon-button';

export type IconButtonProps = IconButtonStyledProps & {
  loading?: boolean;
  stopPropagation?: boolean;
  pressed?: boolean;
  bi?: BIEventProps;
  kind?: IconButtonStyledProps['variant'];
};

const IconButtonLoadingSpinner = () => (
  <Center
    inline
    position="absolute"
    transform="translate(-50%, -50%)"
    top="50%"
    insetStart="50%"
  >
    <Spinner
      borderTopColor="currentColor"
      borderRightColor="currentColor"
      color="currentColor"
    />
  </Center>
);

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (props, ref) => {
    const {
      loading,
      disabled,
      pressed,
      children,
      bi,
      stopPropagation,
      onClick,
      onPointerDown,
      kind,
      variant,
      ...rest
    } = props;

    const { eventType = BIEvents.CLICK, eventData } = bi ?? {};

    const handleClick: IconButtonStyledProps['onClick'] = (event) => {
      if (stopPropagation) {
        event.stopPropagation();
      }

      onClick?.(event);
    };

    const handlePointerDown: IconButtonStyledProps['onPointerDown'] = (
      event,
    ) => {
      if (stopPropagation) {
        event.stopPropagation();
      }

      onPointerDown?.(event);
    };

    return (
      <StyledIconButton
        ref={ref}
        {...rest}
        variant={variant ?? kind}
        disabled={Boolean(loading || disabled)}
        data-loading={loading ? '' : undefined}
        aria-busy={loading || undefined}
        aria-pressed={pressed}
        {...(pressed === undefined
          ? {}
          : { 'data-active': pressed ? '' : undefined })}
        data-bi-unmask="true"
        data-analytics-type={eventType}
        data-analytics-props={serializeBIEventData(eventData)}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
      >
        {loading ? (
          <>
            <IconButtonLoadingSpinner />
            <styled.span opacity={0}>{children}</styled.span>
          </>
        ) : (
          children
        )}
      </StyledIconButton>
    );
  },
);

IconButton.displayName = 'IconButton';
